import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { useAuth } from '../contexts/AuthContext';
import { C } from '../theme';

const pageSx = {
  pt: '110px',
  pb: 8,
  minHeight: '100vh',
  backgroundColor: C.bg,
  backgroundImage: 'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(243,192,21,0.10), transparent 70%)',
};
const paperSx = { p: { xs: 3, md: 5 }, backgroundColor: C.surface, borderRadius: 4, border: `1px solid ${C.line}` };

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [full_name, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userType, setUserType] = useState('');
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Lozinke se ne poklapaju');
      setLoading(false);
      return;
    }

    if (!full_name || !email || !password || !userType) {
      setError('Sva polja su obavezna');
      setLoading(false);
      return;
    }

    if (!eulaAccepted) {
      setError('Morate prihvatiti uslove korišćenja da biste nastavili');
      setLoading(false);
      return;
    }

    try {
      await register({ full_name, email, password, user_type: userType });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Registracija nije uspela');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={pageSx}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 1.5, color: C.white, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Registracija uspešna!
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ ...paperSx, textAlign: 'center' }}>
            <EmailIcon sx={{ fontSize: 80, color: C.gold, mb: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
              Dobrodošli na Nađi Investitora!
            </Typography>
            <Typography variant="body1" sx={{ color: C.muted, mb: 4, lineHeight: 1.6 }}>
              Vaš nalog je uspešno kreiran. Poslali smo vam email sa linkom za verifikaciju vaše email adrese.
              Molimo vas da proverite vašu email poštu i kliknete na link za verifikaciju.
            </Typography>
            <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
              <strong>Važno:</strong> Ako ne vidite email, proverite vaš spam folder.
              Link za verifikaciju je važeći 24 sata.
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={() => navigate('/login')}>
                Prijavite se
              </Button>
              <Button variant="contained" onClick={() => navigate('/')}>
                Nastavite na početnu
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 1.5, color: C.white, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Registracija
          </Typography>
          <Typography variant="h6" sx={{ color: C.muted, maxWidth: 500, mx: 'auto', lineHeight: 1.6, fontWeight: 400, fontSize: '1.05rem' }}>
            Kreirajte svoj nalog i započnite sa povezivanjem sa investitorima ili vlasnicima zemljišta
          </Typography>
        </Box>

        <Paper elevation={0} sx={paperSx}>
          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField required fullWidth label="Ime i prezime" value={full_name} onChange={(e) => setFullName(e.target.value)} placeholder="Unesite vaše ime i prezime" />
              </Grid>

              <Grid item xs={12}>
                <TextField required fullWidth label="Email adresa" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vasa.email@primer.com" />
              </Grid>

              <Grid item xs={12}>
                <TextField required fullWidth label="Lozinka" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Unesite lozinku" />
              </Grid>

              <Grid item xs={12}>
                <TextField required fullWidth label="Potvrdite lozinku" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Ponovo unesite lozinku" />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Tip korisnika</InputLabel>
                  <Select value={userType} label="Tip korisnika" onChange={(e) => setUserType(e.target.value)}>
                    <MenuItem value="land_owner">Vlasnik zemljišta</MenuItem>
                    <MenuItem value="business_owner">Vlasnik biznisa</MenuItem>
                    <MenuItem value="investor">Investitor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={eulaAccepted}
                      onChange={(e) => setEulaAccepted(e.target.checked)}
                      sx={{ color: eulaAccepted ? C.gold : C.muted, '&.Mui-checked': { color: C.gold } }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: C.muted }}>
                      Prihvatam{' '}
                      <Link component={RouterLink} to="/eula" target="_blank" sx={{ color: '#F6CB4B', textDecoration: 'none', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
                        uslove korišćenja
                      </Link>
                      {' '}i razumem da postoji nulta tolerancija na neprikladne sadržaje i zlostavljanje korisnika
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 1, py: 1.5, fontSize: '1.05rem' }}>
                  {loading ? <CircularProgress size={24} sx={{ color: C.onGold }} /> : 'Registrujte se'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" sx={{ color: C.muted }}>
              Već imate nalog?{' '}
              <Link component={RouterLink} to="/login" sx={{ color: '#F6CB4B', textDecoration: 'none', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
                Prijavite se ovde
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;
